import { useMemo } from 'react';
import { defaultNavItems } from '../config/dashboardNavigation';
import { defaultSettingsItems } from '../config/settingsNavigation';
import { useFeatureFlags } from '../../../_features/featureFlags/hooks/useFeatureFlags';
import { filterNavItemsByFeatureFlags } from '../../../_features/featureFlags/utils/featureFlags';
import { MENU_FEATURE_MAP } from '../../../_features/featureFlags/config/featureFlagConfig';
import { shouldShowJsonViewer, shouldShowComponentLibrary } from '../utils/environment';
import { useGetNavCountsQuery } from '../api/navCountsApi';
import { useOrgStats } from '../../../_hooks/useOrgStats';

const getBaseNavigation = (isSettingsMode, externalNavItems) => {
  return externalNavItems || (isSettingsMode ? defaultSettingsItems : defaultNavItems);
};

const applyEnvironment = (baseNavItems, isSettingsMode) => {
  let navItems = baseNavItems;
  if (!isSettingsMode && shouldShowJsonViewer()) {
    navItems = [
      ...navItems,
      {
        menuItem: "JSON Viewer",
        path: "/json-viewer",
        icon: { source: "additional", name: "Code" },
      }
    ];
  }
  if (!isSettingsMode && shouldShowComponentLibrary()) {
    navItems = [
      ...navItems,
      {
        menuItem: "Component Library",
        path: "/component-library",
        icon: { source: "additional", name: "Blocks" },
      }
    ];
  }
  return navItems;
};

const applyFeatureFlags = (navItems, featureFlags) => {
  return filterNavItemsByFeatureFlags(navItems, featureFlags, MENU_FEATURE_MAP);
};

// "Invoices" is the one badge that isn't a bare number (design's "6 due") -
// every other `metaCountKey` just stringifies the count as-is.
const NAV_COUNT_FORMATTERS = {
  invoicesDue: (count) => `${count} due`,
};

// Resolves each item's `metaCountKey` (see dashboardNavigation.js) into a
// real `meta` badge string once GET /nav-counts has loaded - items with no
// `metaCountKey` (or one this response has no field for) pass through
// unchanged, same as before real counts existed (no badge at all).
const applyNavCounts = (navItems, navCounts) => {
  if (!navCounts) return navItems;
  const resolveItem = (item) => {
    let next = item;
    if (item.metaCountKey && navCounts[item.metaCountKey] !== undefined) {
      const format = NAV_COUNT_FORMATTERS[item.metaCountKey] || String;
      next = { ...item, meta: format(navCounts[item.metaCountKey]) };
    }
    if (next.ddItems) {
      next = { ...next, ddItems: next.ddItems.map(resolveItem) };
    }
    return next;
  };
  return navItems.map(resolveItem);
};

export const useResolvedNavigation = (isSettingsMode, externalNavItems) => {
  const { featureFlags, isLoading } = useFeatureFlags();
  // Same useOrgStats cache (localStorage-backed) as the Companies/Plans/
  // Revenue stat cards - the nav rail mounts once for the whole app shell,
  // so this paints from the last known counts immediately on load instead
  // of showing no badges at all until the first fetch resolves.
  const { stats: navCounts } = useOrgStats(useGetNavCountsQuery, "navCounts");

  return useMemo(() => {
    let items = getBaseNavigation(isSettingsMode, externalNavItems);
    items = applyEnvironment(items, isSettingsMode);
    // Fail open while flags haven't loaded yet — filtering against an empty
    // {} would hide every flag-gated item instead of showing them all.
    if (!isLoading) {
      items = applyFeatureFlags(items, featureFlags);
    }
    items = applyNavCounts(items, navCounts);
    return items;
  }, [externalNavItems, isSettingsMode, featureFlags, isLoading, navCounts]);
};
